import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useTransactions, useBankAccount } from '../hooks';
import { TransactionStatistics } from '../types';
import { apiClient } from '../api/client';
import Text from '../components/Text/Text';
import LabeledValue from '../components/LabeledValue';
import BankAccountForm from '../components/BankAccount/BankAccountForm';
import BankAccountDetails from '../components/BankAccount/BankAccountDetails';
import OnboardingButton from '../components/StripeConnect/OnboardingButton';
import VerificationStatus from '../components/StripeConnect/VerificationStatus';
import { getOnboardingStatusString } from '../utils/stripeUtils';
import './ProfileScreen.css';

const ProfileScreen: React.FC = () => {
  const { currentUser } = useAuth();
  // We're not using transactions directly, but we might need it in the future
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [statistics, setStatistics] = useState<TransactionStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const {
    primaryBankAccount,
    isLoading: isBankAccountLoading,
    refreshBankAccountDetails
  } = useBankAccount();
  
  // Use ref to track if data has been fetched
  const hasLoadedStatistics = useRef(false);
  
  // Format date to a more readable format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  console.log('ProfileScreen rendered with currentUser:', currentUser);
  
  // Fetch transaction statistics
  useEffect(() => {
    if (!hasLoadedStatistics.current) {
      const fetchStatistics = async () => {
        try {
          setIsLoading(true);
          const data = await apiClient.transactions.getTransactionStatistics();
          setStatistics(data as TransactionStatistics);
          hasLoadedStatistics.current = true;
        } catch (error) {
          console.error('Error fetching transaction statistics:', error);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchStatistics();
    }
  }, []);
  
  // Handle bank account form success
  const handleBankAccountSuccess = () => {
    // Refresh bank account details
    refreshBankAccountDetails();
    setShowUpdateForm(false);
  };
  
  // Handle update button click
  const handleUpdateClick = () => {
    setShowUpdateForm(true);
  };
  
  // Handle cancel button click
  const handleCancelClick = () => {
    setShowUpdateForm(false);
  };
  
  // Render bank account section
  const renderBankAccountSection = () => {
    if (!currentUser?.stripeDetails?.stripeConnectedAccountId) {
      return (
        <>
          <Text className="text-caption">No Bank Account Connected</Text>
          <Text className="text-caption">
            Your Account is being created. Please check back later.
          </Text>
        </>
      );
    }
    
    const onboardingStatusString = getOnboardingStatusString(currentUser.stripeDetails.onboardingStatus);

    // Check if onboarding is complete before showing bank account section
    if (onboardingStatusString !== 'Complete') {
      return (
        <>
          {/* <Text className="text-warning">Bank Account Management Unavailable</Text> */}
          <div>
            <Text className="text-warning">
              Please complete the account onboarding process to receive payments and manage your bank account.
            </Text>
            {/* Stripe Connect Onboarding */}
            <div id="stripe-connect-onboarding">
              <div>
                {/* <Text className="text-caption">
                  Complete your account setup to receive payments.
                </Text> */}
                <OnboardingButton />
                <VerificationStatus />
              </div>
            </div>
          </div>
        </>
      );
    }
    
    if (isBankAccountLoading) {
      return <Text className="text-caption">Loading bank account details...</Text>;
    }
    
    if (primaryBankAccount && !showUpdateForm) {
      return (
        <BankAccountDetails
          accountHolderName={primaryBankAccount.accountHolderName}
          routingNumber={primaryBankAccount.routingNumber}
          last4={primaryBankAccount.last4}
          bankName={primaryBankAccount.bankName}
          onUpdateClick={handleUpdateClick}
        />
      );
    }
    
    if (showUpdateForm) {
      return (
        <BankAccountForm
          isUpdate={true}
          defaultValues={{
            accountHolderName: primaryBankAccount?.accountHolderName,
            accountHolderType: primaryBankAccount?.accountHolderType as 'individual' | 'company',
          }}
          onSuccess={handleBankAccountSuccess}
          onCancel={handleCancelClick}
          onError={(error) => {
            console.error('Bank account update error:', error);
          }}
        />
      );
    }
    
    return (
      <>
        <Text className="text-caption">
          You can add your bank account details below
        </Text>
        <BankAccountForm
          onSuccess={handleBankAccountSuccess}
          onError={(error) => {
            console.error('Bank account error:', error);
          }}
        />
      </>
    );
  };
  
  // Render pie chart for transaction type distribution
  const renderPieChart = () => {
    if (!statistics || !statistics.transactionTypeDistribution || statistics.transactionTypeDistribution.length === 0) {
      return <div className="placeholder-chart">No transaction data available</div>;
    }
    
    const colors = ['#8a2be2', '#6c757d', '#2e8b57', '#dc3545', '#ffc107', '#17a2b8'];
    const total = statistics.transactionTypeDistribution.reduce((sum, item) => sum + item.count, 0);
    
    return (
      <div className="pie-chart">
        <svg viewBox="0 0 100 100">
          {statistics.transactionTypeDistribution.map((item, index) => {
            // Calculate the percentage and angle for this segment
            const percentage = item.count / total;
            const startAngle = index === 0 ? 0 : statistics.transactionTypeDistribution
              .slice(0, index)
              .reduce((sum, prev) => sum + (prev.count / total) * 360, 0);
            const endAngle = startAngle + (percentage * 360);
            
            // Convert angles to radians and calculate coordinates
            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (endAngle - 90) * Math.PI / 180;
            
            const x1 = 50 + 40 * Math.cos(startRad);
            const y1 = 50 + 40 * Math.sin(startRad);
            const x2 = 50 + 40 * Math.cos(endRad);
            const y2 = 50 + 40 * Math.sin(endRad);
            
            // Determine if the arc should be drawn as a large arc
            const largeArcFlag = percentage > 0.5 ? 1 : 0;
            
            // Create the SVG path for this segment
            const pathData = `
              M 50 50
              L ${x1} ${y1}
              A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}
              Z
            `;
            
            return (
              <path
                key={index}
                d={pathData}
                fill={colors[index % colors.length]}
                stroke="#fff"
                strokeWidth="0.5"
              />
            );
          })}
        </svg>
        
        <div className="pie-chart-legend">
          {statistics.transactionTypeDistribution.map((item, index) => (
            <div key={index} className="legend-item">
              <div className="legend-color" style={{ backgroundColor: colors[index % colors.length] }}></div>
              <div className="legend-label">{item.type}: {item.count}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div className="profile-container">
      {/* <div className="profile-header">
        <Text className="text-h1">Your Profile</Text>
      </div> */}
      
      {/* User Information Section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <Text className="text-h2">User Information</Text>
        </div>
        <div className="escrow-card-section">
          <div className="section-header">
              <Text className="text-body"> Your VeriPay ID</Text>
          </div>
          <LabeledValue label='' 
            value={currentUser?.userId || 'Not set'} 
            copyable={true} className='veripay-id'/>
        </div>
        <div className="profile-section-content">
          <LabeledValue
            label="Username"
            value={currentUser?.name || 'Not available'} multiline={true}
          />
          <LabeledValue
            label="Account Created"
            value={currentUser?.createdAt ? formatDate(currentUser.createdAt) : 'Not available'}
            multiline={true}
          />
        </div>
      </div>
      
      {/* Account Management Section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <Text className="text-h2">Account Management</Text>
        </div>
        <div className="profile-section-content">
          {/* Password Reset Placeholder */}
          <div className="placeholder-component">
            <div className="placeholder-header">
              <Text className="text-body">Password Reset</Text>
            </div>
            <div className="placeholder-content">
              <Text className="text-caption">Coming Soon. </Text>
              <Text className="text-caption">This feature is under development</Text>
            </div>
          </div>
          
          {/* Bank Account Details */}
          <div id="bank-account" className="placeholder-component">
            <div className="placeholder-header">
              <Text className="text-body">Bank Account</Text>
            </div>
            <div>
              {renderBankAccountSection()}
            </div>
          </div>
          
          {/* Notification Settings */}
          <div className="placeholder-component">
            <div className="placeholder-header">
              <Text className="text-body">Notification Settings</Text>
            </div>
            <div className="placeholder-content">
              <Text className="text-caption">
                Manage how and when you receive notifications from VeriPay
              </Text>
              <div className="action-button-container">
                <Link to="/profile/notifications" className="action-link">
                  Manage Notification Settings
                </Link>
              </div>
            </div>
          </div>
          
          {/* Address Information Placeholder */}
          <div className="placeholder-component">
            <div className="placeholder-header">
              <Text className="text-body">Address Information</Text>
            </div>
            <div className="placeholder-content">
              <Text className="text-caption">Coming Soon</Text>
              <Text className="text-caption">This feature is under development</Text>
            </div>
          </div>
        </div>
      </div>
      
      {/* Transaction Summary Section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <Text className="text-h2">Transaction Summary</Text>
        </div>
        <div className="section-content">
          {isLoading ? (
            <div className="loading-state">
              <Text className="text-body">Loading transaction statistics...</Text>
            </div>
          ) : (
            <>
              <div className="stats-row">
                <div className="stat-item">
                  <LabeledValue label='Total Transactions' value={statistics?.totalCompletedTransactions || 0} />
                </div>
                <div className="stat-item">
                  <LabeledValue label='Total Value' value={(statistics?.totalTransactionValue || 0).toFixed(2)} />
                </div>
              </div>
              
              {/* <div className="chart-container">
                <Text className="text-body">Transaction Types</Text>
                {renderPieChart()}
              </div> */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;